import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Layers, User, Building2, Send } from "lucide-react";

interface PositioningCardProps {
  messageType: string | null;
  deliveryChannel: string | null;
  senderName: string | null;
  contactName?: string | null;
  contactTitle?: string | null;
  accountName?: string | null;
  generationMetadata?: Record<string, any> | null;
}

export function PositioningCard({
  messageType,
  deliveryChannel,
  senderName,
  contactName,
  contactTitle,
  accountName,
  generationMetadata,
}: PositioningCardProps) {
  const model = generationMetadata?.model as string | undefined;
  const promptVersion = generationMetadata?.prompt_version as string | undefined;

  return (
    <Card className="border-dashed">
      <CardHeader className="pb-2 pt-3 px-4">
        <CardTitle className="text-xs font-medium uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
          <Layers className="h-3.5 w-3.5" />
          Positioning & Delivery
        </CardTitle>
      </CardHeader>
      <CardContent className="px-4 pb-3">
        <div className="grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
          {messageType && (
            <div>
              <span className="text-xs text-muted-foreground">Message Type</span>
              <div className="font-medium">{formatType(messageType)}</div>
            </div>
          )}
          {deliveryChannel && (
            <div>
              <span className="text-xs text-muted-foreground">Channel</span>
              <Badge variant="outline" className="text-[11px]">
                {deliveryChannel === "email" ? "Email" : "LinkedIn"}
              </Badge>
            </div>
          )}
          {senderName && (
            <div>
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <Send className="h-3 w-3" />
                Sender
              </span>
              <div className="font-medium">{senderName}</div>
            </div>
          )}
          {contactName && (
            <div>
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <User className="h-3 w-3" />
                Recipient
              </span>
              <div className="font-medium truncate">{contactName}</div>
              {contactTitle && (
                <div className="text-xs text-muted-foreground truncate">{contactTitle}</div>
              )}
            </div>
          )}
          {accountName && (
            <div>
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <Building2 className="h-3 w-3" />
                Company
              </span>
              <div className="font-medium truncate">{accountName}</div>
            </div>
          )}
          {model && (
            <div>
              <span className="text-xs text-muted-foreground">Model</span>
              <div className="text-xs text-muted-foreground font-mono">{model}</div>
            </div>
          )}
          {promptVersion && (
            <div>
              <span className="text-xs text-muted-foreground">Prompt</span>
              <div className="text-xs text-muted-foreground font-mono">{promptVersion}</div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function formatType(type: string): string {
  return type
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
